import {
    buildEquipmentRateMatchContext,
    buildLaborRateMatchContext,
    buildMaterialRateMatchContext,
    matchEquipmentRateFromTables,
    matchLaborRateForTimeType,
    matchMaterialRateFromTables,
    SerializedRateTable,
} from "@rhumbix/rmbx_matching"

import { AsyncThunk, BasicTkTimeType, ReduxState } from "../common/types"
import { iEmployee, iEmployeePicklistItem, tEntitySelection } from "../cached-data/types"
import { loadAllReferenceableData, pollStateForCondition } from "../cached-data/actions"
import { fetchProjectRateTableIds } from "../reducers/by-project-cache"
import { selectRateTablesForMatchingById } from "./selectors"
import { selectCohortIdsByEmployeeId } from "./selectors/cohorts"
import { referenceableDataSelector } from "../selectors"
import { FieldFormEquipmentRateMatchParams, FieldFormMaterialRateMatchParams, FieldFormLaborRateMatchParams, LaborRateMatchContextParams, tRateRow } from "./types"

/**
 * Filter credentials (licenses, unions, certifications) to only those valid
 * for the given entry date.
 *
 * A credential is valid when:
 * - Both dates are null -> always valid
 * - issue_date is set -> entryDate must be >= issue_date
 * - expiration_date is set -> entryDate must be < expiration_date
 *
 * @param credentials - Array of credential objects with issue_date and expiration_date
 * @param entryDate - The date string to validate against
 * @returns Filtered array containing only credentials valid for the entry date
 */
export const filterValidCredentials = (credentials: iEmployeePicklistItem[], entryDate: string): iEmployeePicklistItem[] => {
    // String comparison works correctly here because dates are ISO 8601 (YYYY-MM-DD),
    // which sorts lexicographically.
    return credentials.filter(cred => {
        if (cred.issue_date && entryDate < cred.issue_date) return false
        if (cred.expiration_date && entryDate >= cred.expiration_date) return false
        return true
    })
}

/**
 * Return a copy of the employee with licenses, unions, and certifications
 * filtered to only those valid for the given entry date.
 *
 * @param employee - The employee object from referenceableData
 * @param entryDate - The date string to filter credentials against
 * @returns A shallow copy of the employee with filtered credential arrays
 */
export const withValidCredentials = (employee: iEmployee, entryDate: string): iEmployee => ({
    ...employee,
    licenses: filterValidCredentials(employee.licenses ?? [], entryDate),
    unions: filterValidCredentials(employee.unions ?? [], entryDate),
    certifications: filterValidCredentials(employee.certifications ?? [], entryDate),
})

/**
 * Load and return the serialized rate tables for a project in priority order.
 * Handles fetching rate table IDs, loading them into the Redux cache, and
 * polling until they are available.
 *
 * @param projectId - The project ID to load rate tables for
 * @returns Promise resolving to the rate tables array, or null if loading failed
 */
const loadProjectRateTables = (projectId: number): AsyncThunk<SerializedRateTable[] | null> => async (dispatch, getState) => {
    // Fetch rate table IDs for the project (uses cache if available)
    const rateTableIds = await dispatch(fetchProjectRateTableIds(projectId))

    // No rate tables configured for this project
    if (!rateTableIds || rateTableIds.length === 0) {
        return null
    }

    // Ensure rate tables are loaded into Redux cache
    dispatch(loadAllReferenceableData("rateTables", rateTableIds))

    // Wait for rate tables to be loaded into the cache
    const allRateTablesLoaded = await dispatch(
        pollStateForCondition((state: ReduxState) => {
            const loaded = selectRateTablesForMatchingById(state)
            return rateTableIds.every(id => loaded[id] !== undefined)
        })
    )

    if (!allRateTablesLoaded) {
        return null
    }

    // Get rate tables in priority order (order from the project's rate_tables array)
    const rateTablesForMatchingById = selectRateTablesForMatchingById(getState())
    return rateTableIds.map(id => rateTablesForMatchingById[id]).filter((rt): rt is SerializedRateTable => rt !== undefined)
}

/**
 * Check for a labor rate match for the given context parameters and time type.
 * This async thunk fetches rate table IDs for the project, ensures rate tables
 * are loaded, builds the match context, and returns the matched labor rate ID.
 *
 * @param params - The labor rate match context parameters (must include projectId)
 * @param timeType - The time type to match against (ST, OT, or DT)
 * @returns Promise resolving to the matched labor rate ID, or null if no match found
 */
export const checkLaborRateMatch = (params: LaborRateMatchContextParams, timeType: BasicTkTimeType): AsyncThunk<number | null> => async (dispatch, getState) => {
    const { projectId, employeeId, costCodeId, modifiersString, entryDate } = params

    // Project ID is required to fetch rate table IDs.
    if (!projectId) {
        return null
    }

    const rateTables = await dispatch(loadProjectRateTables(projectId))
    if (!rateTables) {
        return null
    }

    // Build the match context from the parameters.
    // Cohort employee data is expected to be preloaded by the parent view (WeeklyTkDetailModal).
    const state: ReduxState = getState()
    const referenceableData: tEntitySelection = referenceableDataSelector(state)

    const rawEmployee = employeeId ? referenceableData.employees[employeeId] : undefined
    const employee = rawEmployee ? withValidCredentials(rawEmployee, entryDate) : undefined
    const costCode = costCodeId ? referenceableData.costCodes[costCodeId] : undefined
    const project = projectId ? referenceableData.projects[projectId] : undefined
    const modifiers = JSON.parse(modifiersString) as Record<string, number>
    const cohortIdsByEmployeeId = selectCohortIdsByEmployeeId(state)

    const matchContext = buildLaborRateMatchContext({
        entryDate,
        employee,
        costCode,
        project,
        modifiers: {
            classification_name: referenceableData.companyClassifications[modifiers?.classification]?.name ?? undefined,
            trade_name: referenceableData.companyTrades[modifiers?.trade]?.name ?? undefined,
            ids: modifiers,
        },
        cohortIds: employeeId ? cohortIdsByEmployeeId[employeeId] ?? [] : [],
    })

    // Missing required data for matching (employee or project not found)
    if (!matchContext) {
        return null
    }

    // Perform the matching
    const matchedLaborRate = matchLaborRateForTimeType(rateTables, matchContext, timeType)

    return matchedLaborRate?.id ?? null
}

/**
 * Match a labor rate for a field form table row and return the full rate row object.
 * Unlike checkLaborRateMatch (which returns just an ID for timekeeping), this returns
 * the complete rate_row data (id, name, base_rate, custom_rates) needed by field forms.
 *
 * The employee and cost code objects are passed directly from the form row (stored at
 * selection time), so matching reflects the employee as they were when added to the form.
 *
 * @param params - The field form rate match parameters
 * @param timeType - The time type to match against (ST, OT, DT, TT, POT, or PDT)
 * @returns Promise resolving to a rate_row object, or null if no match found
 */
export const matchLaborRateForFieldForm = (params: FieldFormLaborRateMatchParams, timeType: string): AsyncThunk<tRateRow | null> => async (dispatch, getState) => {
    const { projectId, employee: rawEmployee, costCode, entryDate } = params

    if (!projectId) {
        return null
    }

    const rateTables = await dispatch(loadProjectRateTables(projectId))
    if (!rateTables) {
        return null
    }

    const state: ReduxState = getState()
    const referenceableData: tEntitySelection = referenceableDataSelector(state)

    const employee = rawEmployee ? withValidCredentials(rawEmployee, entryDate) : undefined
    const project = projectId ? referenceableData.projects[projectId] : undefined

    const matchContext = buildLaborRateMatchContext({
        entryDate,
        employee,
        costCode: costCode ?? undefined,
        project,
        modifiers: {
            classification_name: rawEmployee?.classification,
            trade_name: rawEmployee?.trade,
            ids: {},
        },
        cohortIds: rawEmployee?.cohorts?.map(c => c.id) ?? [],
    })

    if (!matchContext) {
        return null
    }

    // The matching library's TS declaration only accepts "ST" | "OT" | "DT", but at runtime
    // it does a simple string comparison (rate.time_type !== context.time_type), so POT/PDT work.
    const matchedRate = matchLaborRateForTimeType(rateTables, matchContext, timeType as BasicTkTimeType)

    if (!matchedRate) {
        return null
    }

    return matchedRate
}

/**
 * Match an equipment rate for a field form table row and return the full rate row object.
 * Equipment rate matching does not use time type — the matching library returns a single
 * rate per equipment/cost code/project context.
 *
 * The equipment and cost code objects are passed directly from the form row (stored at
 * selection time), so matching reflects the equipment as it was when added to the form.
 *
 * @param params - The field form equipment rate match parameters
 * @returns Promise resolving to a rate_row object, or null if no match found
 */
export const matchEquipmentRateForFieldForm = (params: FieldFormEquipmentRateMatchParams): AsyncThunk<tRateRow | null> => async (dispatch, getState) => {
    const { projectId, equipment, costCode, entryDate } = params

    if (!projectId) {
        return null
    }

    const rateTables = await dispatch(loadProjectRateTables(projectId))
    if (!rateTables) {
        return null
    }

    const state: ReduxState = getState()
    const referenceableData: tEntitySelection = referenceableDataSelector(state)

    const project = projectId ? referenceableData.projects[projectId] : undefined

    const matchContext = buildEquipmentRateMatchContext({
        entryDate,
        costCode: costCode ?? undefined,
        project,
        equipment: equipment
            ? {
                  id: equipment.id,
                  status: equipment.status,
                  category: equipment.category,
                  custom_fields: equipment.custom_fields,
              }
            : undefined,
    })

    const matchedRate = matchEquipmentRateFromTables(rateTables, matchContext)

    if (!matchedRate) {
        return null
    }

    return matchedRate
}

/**
 * Match a material rate for a field form table row and return the full rate row object.
 * Material rate matching does not use time type — the matching library returns a single
 * rate per material/cost code/project context.
 *
 * @param params - The field form material rate match parameters
 * @returns Promise resolving to a rate_row object, or null if no match found
 */
export const matchMaterialRateForFieldForm = (params: FieldFormMaterialRateMatchParams): AsyncThunk<tRateRow | null> => async (dispatch, getState) => {
    const { projectId, material, costCode, entryDate } = params

    if (!projectId) {
        return null
    }

    const rateTables = await dispatch(loadProjectRateTables(projectId))
    if (!rateTables) {
        return null
    }

    const state: ReduxState = getState()
    const referenceableData: tEntitySelection = referenceableDataSelector(state)

    const project = projectId ? referenceableData.projects[projectId] : undefined

    const matchContext = buildMaterialRateMatchContext({
        entryDate,
        costCode: costCode ?? undefined,
        project,
        material: material
            ? {
                  id: material.id,
                  group: material.group,
                  part_number: material.part_number,
                  custom_fields: material.custom_fields,
              }
            : undefined,
    })

    const matchedRate = matchMaterialRateFromTables(rateTables, matchContext)

    if (!matchedRate) {
        return null
    }

    return matchedRate
}
